import { FC, type ReactNode, SVGProps, useEffect } from "react";
import {
  ICON_REGISTRY,
  type IconName,
  MONO_ICON_NAMES,
  type MonoIconName,
} from "./generated/registry";

/**
 * Every registry name, for surfaces that have to enumerate them — the Ladle
 * catalog, mainly. Derived from the registry so it cannot drift out of sync;
 * a hand-kept list would need its own completeness test.
 */
export const ICON_NAMES = Object.keys(ICON_REGISTRY) as IconName[];

/**
 * An icon slot: a design-system icon name, or a caller-supplied node.
 *
 * Pairing `IconName` with a bare `ReactNode` would be pointless — `ReactNode`
 * admits any `string`, both directly and structurally as `Iterable<ReactNode>`,
 * so the union collapses and a misspelled name type-checks. Excluding both
 * keeps the name side checked while still accepting a custom element.
 */
export type IconSlot =
  | IconName
  | Exclude<ReactNode, string | Iterable<ReactNode>>;

/**
 * Runtime guard for names that only exist as data (API payloads, config read at
 * runtime). Prefer typing the source over reaching for this.
 */
export const isIconName = (value: string): value is IconName =>
  Object.hasOwn(ICON_REGISTRY, value);

/** Whether a name is one of the glyphs `fill` / `stroke` can paint. */
export const isMonoIconName = (value: IconName): value is MonoIconName =>
  MONO_ICON_NAMES.has(value);

/**
 * `name` + `fill` / `stroke` props for `<Icon>`, from a pair the type can no
 * longer see is valid.
 *
 * A component prop that carries an icon name and a tint (`ToolbarAction.icon` +
 * `color`, `TimelineItemTypeConfig.icon` + `iconColor`) is checked where the
 * consumer writes it. By the time the two reach `<Icon>` they are separate
 * values typed `IconName` and `string | undefined`, which narrows into neither
 * branch of `IconProps` — so the pairing is re-established here, at runtime.
 *
 * On a colour glyph the tint is dropped rather than applied, which is what
 * `<Icon fill>` already did to those glyphs: they paint from their own palette
 * and never read `--icon-fill`.
 */
export function iconPaintProps(
  name: IconName,
  fill?: string,
  stroke?: string,
):
  | { name: IconName; fill?: never; stroke?: never }
  | { name: MonoIconName; fill?: string; stroke?: string } {
  return isMonoIconName(name) ? { name, fill, stroke } : { name };
}

/** Props that mean the same thing whatever the glyph is. */
interface IconCommonProps {
  /**
   * Semantic size based on design tokens
   * 2xs: 10px, xs: 12px, s: 16px, m: 20px, l: 24px
   * Takes precedence over sizeX/sizeY when provided
   */
  size?: IconSize;

  /**
   * Optional width in pixels
   * If provided, maintains aspect ratio unless height is also specified
   */
  sizeX?: number;

  /**
   * Optional height in pixels
   * If provided, maintains aspect ratio unless width is also specified
   */
  sizeY?: number;

  /**
   * Optional class name for custom styling
   */
  className?: string;

  /**
   * Any additional SVG props to pass to the underlying SVG element
   */
  svgProps?: SVGProps<SVGSVGElement>;
}

/**
 * Icon component props.
 *
 * `fill` / `stroke` are offered only on the mono glyphs, which are the ones
 * drawn entirely in `--icon-fill` / `--icon-stroke`. On a colour glyph they
 * would repaint part of a fixed palette and leave the rest, so the colour
 * branch forbids them.
 *
 * The no-colour branch says `fill?: never` rather than just leaving the props
 * out: an omitted prop is only caught by JSX excess-property checking, so a
 * spread or forwarded props object carrying a colour name plus `fill` would
 * compile clean. `never` rejects both spellings.
 *
 * That branch takes the whole `IconName` union rather than only `ColorIconName`,
 * which is what keeps the many `<Icon name={someIconName} />` call sites
 * working: a variable typed as the full union narrows into neither a mono-only
 * nor a colour-only branch, so splitting strictly by kind would have made every
 * data-driven name an error. Narrowing is only demanded where a colour is
 * actually being passed — which is the case that can go wrong.
 */
export type IconProps = IconCommonProps &
  (
    | {
        /** Any glyph, when the caller is not colouring it. */
        name: IconName;
        fill?: never;
        stroke?: never;
      }
    | {
        /** A glyph whose paint the `fill` / `stroke` props drive. */
        name: MonoIconName;

        /**
         * Fill color for the icon
         * Default is "currentColor" which inherits from parent text color
         */
        fill?: string;

        /**
         * Stroke color for the icon
         * Default is "currentColor"; falls back to `fill` where the glyph is
         * stroke-drawn
         */
        stroke?: string;
      }
  );

/**
 * Semantic icon size to pixel value mapping
 */
export const ICON_SIZE_PX = {
  "2xs": 10,
  xs: 12,
  s: 16,
  m: 20,
  l: 24,
} as const;

export type IconSize = keyof typeof ICON_SIZE_PX;

/**
 * Icon component
 *
 * A unified interface for rendering icons from our design system.
 * Automatically handles viewBox scaling to maintain proper aspect ratio.
 *
 * Usage:
 * <Icon name="search" />                    - Renders with default size
 * <Icon name="search" sizeX={24} />         - Renders at 24px width, height scales proportionally
 * <Icon name="search" sizeX={24} sizeY={24} /> - Renders at exact 24x24 dimensions
 * <Icon name="search" fill="red" />         - Renders with red fill color
 * <Icon name="spinner" stroke="#0088cc" />  - Renders with custom stroke color
 */
export const Icon: FC<IconProps> = (props) => {
  const {
    name,
    size,
    sizeX,
    sizeY,
    className = "",
    svgProps = {},
    fill = "currentColor",
    stroke = "currentColor",
  } = props;

  // Inject spinner styles on client-side only
  useEffect(() => {
    const styleId = "indigo-icon-spinner-styles";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = spinnerStyles;
      document.head.appendChild(style);
    }
  }, []);

  // Get icon definition from registry
  const iconDef = ICON_REGISTRY[name];

  if (!iconDef) {
    console.error(`Icon with name "${name}" not found in registry`);
    return null;
  }

  const { viewBox, defaultSize, path } = iconDef;

  // Calculate dimensions while preserving aspect ratio
  // Priority: semantic size > explicit sizeX/sizeY > default size > viewBox size
  const width =
    (size && ICON_SIZE_PX[size]) ||
    sizeX ||
    (sizeY
      ? (sizeY * viewBox.width) / viewBox.height
      : defaultSize?.width || viewBox.width);
  const height =
    (size && ICON_SIZE_PX[size]) ||
    sizeY ||
    (sizeX
      ? (sizeX * viewBox.height) / viewBox.width
      : defaultSize?.height || viewBox.height);

  // Style props that should be applied directly to the SVG element
  const combinedProps: SVGProps<SVGSVGElement> = { ...svgProps };

  // Add spinner animation class for spinner icons
  const spinnerClass = name === "spinner" ? "icon-spinner" : "";
  const finalClassName = [className, spinnerClass].filter(Boolean).join(" ");

  // Note: We do NOT set fill and stroke on the SVG container element
  // This would cause icons to appear thicker because SVG container would get
  // both fill and stroke. Instead, we let the individual path elements
  // keep their original styling which already uses currentColor

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${viewBox.width} ${viewBox.height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={finalClassName}
      {...combinedProps}
      style={{
        // Apply fill and stroke as CSS variables that child elements can inherit
        // This is more reliable than trying to clone and modify React elements
        ...(combinedProps.style || {}),
        ...(fill !== "currentColor" && { "--icon-fill": fill }),
        ...(stroke !== "currentColor" && { "--icon-stroke": stroke }),
      }}
    >
      {path}
    </svg>
  );
};

/* Spinner animation styles */
const spinnerStyles = `
  @keyframes icon-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  .icon-spinner {
    animation: icon-spin 1s linear infinite;
  }
`;
